// Active project — single source of truth hook.
//
// Both surfaces that touch active project (TopNav `ActiveProjectIndicator` and
// the `ProjectsPage` sidebar) consume this hook. Reads the conversation's
// session meta; a switch made elsewhere (the agent's `project_set_active`,
// another device) arrives as a `conversations` live event (useLiveEvents),
// with the safety-net poll behind it. Writes go through `PUT /api/chat/sessions/:id/active-project`,
// which also moves the server `activeCwd` to the project's `meta.cwd` — one
// network call switches both surfaces and the file tree in lockstep.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GENERAL_PROJECT } from '@onestone/shared/general';
import { api } from './api';
import { useChatStore } from './chatStore';
import { SSE_SAFETY_NET_POLL_MS } from './uiConstants';
import type { SessionMetaResponse } from '@onestone/shared/responses';

export interface UseActiveProject {
  /** The conversation's project — General until it's switched (and before the first message). */
  activeProject: string;
  /** True while the session-meta query is in-flight. */
  isLoading: boolean;
  /** True when the underlying session-meta fetch is enabled (a conversation exists). */
  enabled: boolean;
  /** Switch the active project. Triggers cwd side-effect server-side. */
  setActive: (project: string) => Promise<void>;
  /** True while a `setActive` mutation is in-flight. */
  isSetting: boolean;
}

export function useActiveProject(): UseActiveProject {
  const { conversationId } = useChatStore();
  const queryClient = useQueryClient();
  const enabled = conversationId.length > 0;

  const sessionKey = ['session-meta', conversationId] as const;
  const session = useQuery({
    queryKey: sessionKey,
    queryFn: () => api<SessionMetaResponse>(`/api/chat/sessions/${encodeURIComponent(conversationId)}`),
    enabled,
    refetchInterval: SSE_SAFETY_NET_POLL_MS,
    // A brand-new conversation 404s until its first message; it starts in General.
    retry: false,
  });
  const activeProject = session.data?.meta?.activeProject ?? GENERAL_PROJECT;

  const setProject = useMutation({
    mutationFn: (project: string) =>
      api<{ meta: { activeProject: string }; cwd: string | null }>(
        `/api/chat/sessions/${encodeURIComponent(conversationId)}/active-project`,
        { method: 'PUT', json: { project } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKey });
      // The file tree derives its root from the active project on read
      // (core/activeCwd.ts) — refetch it so it follows the switch now.
      void queryClient.invalidateQueries({ queryKey: ['files', 'cwd'] });
      void queryClient.invalidateQueries({ queryKey: ['files'] });
    },
  });

  return {
    activeProject,
    isLoading: enabled && session.isLoading,
    enabled,
    setActive: async (project: string): Promise<void> => {
      await setProject.mutateAsync(project);
    },
    isSetting: setProject.isPending,
  };
}
